import React, { useState } from "react";
import ResponsiveWrapper from "../../../../../components/common/ResponsiveWrapper";
import FeedList from "./FeedList";
import AddFeedForm from "./AddFeedForm";
import SuccessAnimation from "./SuccessAnimation";
import DeleteSuccessAnimation from "./components/DeleteSuccessAnimation";
import { useUser } from "../../../../../context/UserContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createUserFeed, updateUserFeed, deleteUserFeed } from "../../../../../services/UserFeedService";

const drawerStates = {
    LIST: "LIST",
    ADD: "ADD",
    SUCCESS: "SUCCESS",
    DELETE_SUCCESS: "DELETE_SUCCESS",
};

const initialForm = {
    name: "",
    zipcode: "",
    type: ["house", "condo"],
    price: { min: "", max: "" },
    beds: { min: "", max: "" },
    bath: { min: "", max: "" },
};

const CustomFeedDrawer = ({ show, onHide, feeds }) => {
    const { userState } = useUser();
    const userId = userState?.id;
    const queryClient = useQueryClient();
    const [drawerState, setDrawerState] = useState(drawerStates.LIST);
    const [form, setForm] = useState(initialForm);
    const [isEdit, setIsEdit] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [lastDeletedFeed, setLastDeletedFeed] = useState(null);
    const [currentFeedId, setCurrentFeedId] = useState(null);

    const transformFormToPayload = (feedData) => {
        const payload = {
            name: feedData.name || "",
        };

        // Handle zipcodes from both possible sources
        if (Array.isArray(feedData.zipcodes) && feedData.zipcodes.length > 0) {
            payload.zipCodes = feedData.zipcodes.map((zip) => zip.toString());
        } else if (feedData.zipcode && feedData.zipcode.trim() !== "") {
            payload.zipCodes = [feedData.zipcode.trim()];
        }

        // Convert property types to lowercase and filter valid types
        if (Array.isArray(feedData.type) && feedData.type.length > 0) {
            const propertyTypes = feedData.type
                .map((type) => type.toLowerCase())
                .filter((type) => ["house", "condo"].includes(type));
            if (propertyTypes.length > 0) {
                payload.propertyTypes = propertyTypes;
            }
        }

        // Handle price values from both possible sources
        let minPrice = null;
        let maxPrice = null;

        // Try priceMin/priceMax first
        if (feedData.priceMin) {
            const parsedMin = Number(feedData.priceMin);
            if (!isNaN(parsedMin) && parsedMin > 0) {
                minPrice = parsedMin;
            }
        } else if (feedData.price?.min) {
            const parsedMin = Number(feedData.price.min);
            if (!isNaN(parsedMin) && parsedMin > 0) {
                minPrice = parsedMin;
            }
        }

        if (feedData.priceMax) {
            const parsedMax = Number(feedData.priceMax);
            if (!isNaN(parsedMax) && parsedMax > 0) {
                maxPrice = parsedMax;
            }
        } else if (feedData.price?.max) {
            const parsedMax = Number(feedData.price.max);
            if (!isNaN(parsedMax) && parsedMax > 0) {
                maxPrice = parsedMax;
            }
        }

        // Only include price if both min and max are valid
        if (minPrice !== null && maxPrice !== null) {
            // Ensure maxPrice is not less than minPrice
            payload.minPrice = minPrice;
            payload.maxPrice = Math.max(minPrice, maxPrice);
        }

        // Convert beds and baths to numbers, only if they're not empty
        if (feedData.beds?.min !== "" && feedData.beds?.min !== undefined) {
            const minBeds = Number(feedData.beds.min);
            if (!isNaN(minBeds)) {
                payload.minBeds = minBeds;
            }
        }

        if (feedData.beds?.max !== "" && feedData.beds?.max !== undefined) {
            const maxBeds = Number(feedData.beds.max);
            if (!isNaN(maxBeds)) {
                payload.maxBeds = maxBeds;
            }
        }

        if (feedData.bath?.min !== "" && feedData.bath?.min !== undefined) {
            const minBaths = Number(feedData.bath.min);
            if (!isNaN(minBaths)) {
                payload.minBaths = minBaths;
            }
        }

        if (feedData.bath?.max !== "" && feedData.bath?.max !== undefined) {
            const maxBaths = Number(feedData.bath.max);
            if (!isNaN(maxBaths)) {
                payload.maxBaths = maxBaths;
            }
        }

        return payload;
    };

    const createMutation = useMutation({
        mutationFn: (feedData) => {
            const payload = transformFormToPayload(feedData);
            return createUserFeed(userId, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userFeeds", userId] });
            queryClient.invalidateQueries({ queryKey: ["live-variant-customFeed-properties", userId] });
            setForm(initialForm);
            setDrawerState(drawerStates.SUCCESS);
        },
        onError: (error) => {
            console.error("Error creating feed:", error);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (feedData) => {
            const payload = transformFormToPayload(feedData);
            payload.id = currentFeedId;
            return updateUserFeed(userId, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userFeeds", userId] });
            queryClient.invalidateQueries({ queryKey: ["live-variant-customFeed-properties", userId] });
            setForm(initialForm);
            setDrawerState(drawerStates.SUCCESS);
        },
        onError: (error) => {
            console.error("Error updating feed:", error);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: ({ feedId }) => deleteUserFeed(userId, feedId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["userFeeds", userId] });
            queryClient.invalidateQueries({ queryKey: ["live-variant-customFeed-properties", userId] });
            const deletedFeed = Array.isArray(feeds)
                ? feeds.find((feed) => feed.id === variables.feedId)
                : null;
            setLastDeletedFeed(deletedFeed);
            setDrawerState(drawerStates.DELETE_SUCCESS);
        },
        onError: (error) => {
            console.error("Error deleting feed:", error);
        },
    });

    const handleAdd = () => {
        setForm(initialForm);
        setIsEdit(false);
        setCurrentFeedId(null);
        setCurrentStep(0);
        setDrawerState(drawerStates.ADD);
    };

    const handleEdit = (feed) => {
        setCurrentFeedId(feed.id);

        // Transform backend data to match form structure
        setForm({
            name: feed.name || "",
            // Use the first zipcode from the array or empty string
            zipcode: Array.isArray(feed.zipCodes) && feed.zipCodes.length > 0 ? feed.zipCodes[0] : "",
            // Map property types to match the form's expected format
            type: Array.isArray(feed.propertyTypes)
                ? feed.propertyTypes.map((type) => type.charAt(0).toUpperCase() + type.slice(1)) // Capitalize first letter
                : [],
            // Map price values - ensure they're strings and handle empty values
            price: {
                min: feed.minPrice ? feed.minPrice.toString() : "",
                max: feed.maxPrice ? feed.maxPrice.toString() : "",
            },
            beds: {
                min: feed.minBeds || "",
                max: feed.maxBeds || "",
            },
            bath: {
                min: feed.minBaths || "",
                max: feed.maxBaths || "",
            },
            // Add additional fields for the form
            zipcodes: Array.isArray(feed.zipCodes) ? feed.zipCodes : [],
            priceMin: feed.minPrice ? feed.minPrice.toString() : "",
            priceMax: feed.maxPrice ? feed.maxPrice.toString() : "",
        });

        setIsEdit(true);
        setCurrentStep(0);
        setDrawerState(drawerStates.ADD);
    };

    const handleDelete = (feed) => {
        if (feed?.id) {
            deleteMutation.mutate({ feedId: feed.id });
        }
    };

    const handleFormChange = (field, value) => {
        setForm((prev) => {
            const newForm = { ...prev };
            if (field.includes(".")) {
                const [parent, child] = field.split(".");
                // Handle numeric fields
                if (parent === "price" || parent === "beds" || parent === "bath") {
                    // Only allow numbers
                    const numericValue = value.replace(/[^0-9]/g, "");
                    newForm[parent] = {
                        ...newForm[parent],
                        [child]: numericValue,
                    };
                } else {
                    newForm[parent] = {
                        ...newForm[parent],
                        [child]: value,
                    };
                }
            } else {
                // Handle zipcode field
                if (field === "zipcode") {
                    // Only allow numbers and limit to 5 digits
                    const numericValue = value.replace(/[^0-9]/g, "").slice(0, 5);
                    newForm[field] = numericValue;
                } else {
                    newForm[field] = value;
                }
            }
            return newForm;
        });
    };

    const handleIncrement = (field) => {
        setForm((prev) => {
            const newForm = { ...prev };
            if (field.includes(".")) {
                const [parent, child] = field.split(".");
                const currentValue = Number(newForm[parent][child]) || 0;
                newForm[parent] = {
                    ...newForm[parent],
                    [child]: (currentValue + 1).toString(),
                };
            } else {
                const currentValue = Number(newForm[field]) || 0;
                newForm[field] = (currentValue + 1).toString();
            }
            return newForm;
        });
    };

    const handleDecrement = (field) => {
        setForm((prev) => {
            const newForm = { ...prev };
            if (field.includes(".")) {
                const [parent, child] = field.split(".");
                const currentValue = Number(newForm[parent][child]) || 0;
                newForm[parent] = {
                    ...newForm[parent],
                    [child]: Math.max(0, currentValue - 1).toString(),
                };
            } else {
                const currentValue = Number(newForm[field]) || 0;
                newForm[field] = Math.max(0, currentValue - 1).toString();
            }
            return newForm;
        });
    };

    const handleZipAdd = () => {
        // Placeholder for zip add logic
    };

    const handleReset = () => {
        if (isEdit && currentFeedId) {
            // If editing, reset to the original feed values using the stored ID
            const currentFeed = feeds.find((feed) => feed.id === currentFeedId);

            if (currentFeed) {
                setForm({
                    name: currentFeed.name || "",
                    zipcode:
                        Array.isArray(currentFeed.zipCodes) && currentFeed.zipCodes.length > 0
                            ? currentFeed.zipCodes[0]
                            : "",
                    type: Array.isArray(currentFeed.propertyTypes)
                        ? currentFeed.propertyTypes.map(
                              (type) => type.charAt(0).toUpperCase() + type.slice(1)
                          )
                        : [],
                    price: {
                        min: currentFeed.minPrice ? currentFeed.minPrice.toString() : "",
                        max: currentFeed.maxPrice ? currentFeed.maxPrice.toString() : "",
                    },
                    beds: {
                        min: currentFeed.minBeds || "",
                        max: currentFeed.maxBeds || "",
                    },
                    bath: {
                        min: currentFeed.minBaths || "",
                        max: currentFeed.maxBaths || "",
                    },
                    zipcodes: Array.isArray(currentFeed.zipCodes) ? currentFeed.zipCodes : [],
                    priceMin: currentFeed.minPrice ? currentFeed.minPrice.toString() : "",
                    priceMax: currentFeed.maxPrice ? currentFeed.maxPrice.toString() : "",
                });
            }
        } else {
            // If adding new feed, reset to initial empty values
            setForm(initialForm);
        }
    };

    const handleSave = () => {
        if (isEdit) {
            updateMutation.mutate(form);
        } else {
            createMutation.mutate(form);
        }
    };

    const handleContinue = () => {
        setDrawerState(drawerStates.LIST);
        onHide();
    };

    const handleBack = () => {
        setDrawerState(drawerStates.LIST);
    };

    const handleDeleteDone = () => {
        setDrawerState(drawerStates.LIST);
    };

    const handleStepChange = (step) => {
        setCurrentStep(step);
    };

    const getDrawerHeight = () => {
        if (drawerState === drawerStates.SUCCESS || drawerState === drawerStates.DELETE_SUCCESS) {
            return "auto";
        }
        if (drawerState === drawerStates.ADD) {
            if (currentStep === 1) {
                return "min-h-[250px] max-h-[350px]";
            }
            return "min-h-[350px] max-h-[80vh]";
        }
        if (drawerState === drawerStates.LIST && (!Array.isArray(feeds) || feeds.length === 0)) {
            return "auto";
        }
        return "80vh";
    };

    return (
        <ResponsiveWrapper show={show} onHide={onHide} showHandle drawerHeight={getDrawerHeight()}>
            {drawerState === drawerStates.LIST && (
                <FeedList
                    feeds={feeds}
                    onAdd={handleAdd}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isEmpty={feeds.length === 0}
                />
            )}
            {drawerState === drawerStates.ADD && (
                <AddFeedForm
                    values={form}
                    onChange={handleFormChange}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onZipAdd={handleZipAdd}
                    onReset={handleReset}
                    onSave={handleSave}
                    onBack={handleBack}
                    isEdit={isEdit}
                    step={currentStep}
                    onStepChange={handleStepChange}
                />
            )}
            {drawerState === drawerStates.SUCCESS && <SuccessAnimation onContinue={handleContinue} />}
            {drawerState === drawerStates.DELETE_SUCCESS && (
                <DeleteSuccessAnimation
                    message={`${lastDeletedFeed?.name || "Feed List"} Deleted!`}
                    onDone={handleDeleteDone}
                />
            )}
        </ResponsiveWrapper>
    );
};

export default CustomFeedDrawer;
